/**
 * Notification Provider with Toast UI
 * Provides toast notifications for Refine operations
 */

import type { NotificationProvider } from "@refinedev/core";

/**
 * Toast notification types
 */
export type ToastType = "success" | "error" | "info" | "warning";

/**
 * Toast notification data
 */
export interface ToastData {
  id: string;
  type: ToastType;
  message: string;
  description?: string;
  duration: number;
}

/**
 * Toast state management
 * Simple in-memory store for active toasts
 */
let toasts: ToastData[] = [];
let listeners: Array<(toasts: ToastData[]) => void> = [];

/**
 * Generate unique toast ID
 */
function generateId(): string {
  return `toast-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

/**
 * Notify all listeners of toast state change
 */
function notifyListeners(): void {
  listeners.forEach((listener) => listener([...toasts]));
}

/**
 * Add a toast notification
 */
export function addToast(toast: Omit<ToastData, "id">): string {
  const id = generateId();
  const newToast: ToastData = { ...toast, id };
  toasts = [...toasts, newToast];
  notifyListeners();

  // Auto-remove after duration
  if (toast.duration > 0) {
    setTimeout(() => removeToast(id), toast.duration);
  }

  return id;
}

/**
 * Remove a toast by ID
 */
export function removeToast(id: string): void {
  toasts = toasts.filter((t) => t.id !== id);
  notifyListeners();
}

/**
 * Subscribe to toast state changes
 */
export function subscribeToToasts(
  listener: (toasts: ToastData[]) => void
): () => void {
  listeners = [...listeners, listener];
  listener([...toasts]);
  return () => {
    listeners = listeners.filter((l) => l !== listener);
  };
}

/**
 * Get current toasts (snapshot)
 */
export function getToasts(): ToastData[] {
  return [...toasts];
}

/**
 * Default toast duration in milliseconds
 */
const DEFAULT_DURATION = 5000;

/**
 * Refine notification provider implementation
 * Integrates with Refine's notification system
 */
export const notificationProvider: NotificationProvider = {
  open: ({ type, message, description, key }) => {
    // Map Refine notification types to toast types
    const toastType: ToastType =
      type === "progress" ? "info" : (type as ToastType);

    addToast({
      type: toastType,
      message: message || "",
      description,
      duration: DEFAULT_DURATION,
    });

    // Return key for potential close operation
    return key;
  },

  close: (key) => {
    if (key) {
      removeToast(key);
    }
  },
};
